import {
  Button,
  FormControl,
  FormHelperText,
  FormLabel,
  OutlinedInput
} from '@mui/material';
import { useErrorMessage } from '@shared/hooks/use-error-message';
import React, { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { LiveTvDialogFormProps } from '@modules/entertainment/models/live-tv.models';
import InfoTooltip from '@shared/components/info-tooltip/info-tooltip';
import useLiveTvStore from '../../store/live-tv.store';
import AppAutocompleteV2 from '@components/app-autocomplete/app-autocomplete-v2.tsx';
import AppDialog from '@shared/components/app-dialog/app-dialog';
import CategoryDialog from '@modules/entertainment/components/category-dialog/category-dialog';
import AppAutocompleteWithActions from '@shared/components/app-autocomplete-with-actions/app-autocomplete-with-actions';
import DeleteDialog from '@shared/components/delete-dialog/delete-dialog';
import { CrudActionEnum } from '@models/app-autocomplete.model.ts';
import {
  trimFormValues,
  validateNotOnlySpaces
} from '@shared/utils/trim-form-values-utils';
import MediaLibraryComponent from '@components/media-library-component/media-library-component.tsx';
import { FileTypeEnum } from '@models/media-library.model.ts';
import {
  handleSortData,
  liveTvErrorMessages,
  playerOptions
} from '@modules/entertainment/utils/live-tv.utils.ts';

const LiveTvDialogForm: React.FC<LiveTvDialogFormProps> = ({
  selectedItem,
  categoryList,
  countryList,
  onSave
}) => {
  const {
    register,
    reset,
    handleSubmit,
    control,
    formState: { errors, isValid }
  } = useForm({ mode: 'onTouched' });

  const getErrorMessage = useErrorMessage();

  const { t } = useTranslation();

  const {
    isLoading,
    createCategory,
    editCategory,
    deleteCategory,
    isCategoryLoading
  } = useLiveTvStore();

  const [showCategoryDialog, setShowCategoryDialog] =
    React.useState<boolean>(false);
  const [showDeleteDialog, setShowDeleteDialog] =
    React.useState<boolean>(false);
  const [categoriesList, setCategoriesList] = React.useState<any[]>([]);
  const [selectedCategoriesValue, setSelectedCategoriesValue] =
    React.useState<any>(null);
  const [selectedCountry, setSelectedCountry] = React.useState<any>(null);
  const [selectedLanguage, setSelectedLanguage] = React.useState<any>(null);
  const [selectedPlayer, setSelectedPlayer] = React.useState<any>(
    playerOptions[0]
  );
  const [selectedMedia, setSelectedMedia] = React.useState<Record<string, any>>(
    {}
  );

  const handleSave = async (data: any) => {
    const trimmedData = trimFormValues(data);
    const updatedItem = handleSortData(trimmedData, categoriesList);
    updatedItem.image = selectedMedia.image?.path || null;

    onSave(updatedItem);
  };

  const handleSaveCategory = async (value: any) => {
    if (selectedCategoriesValue.id) {
      editCategory({
        data: value,
        id: selectedCategoriesValue.id,
        handleClose: handleCloseCategoryDialog
      });
    } else {
      createCategory({
        data: value,
        handleClose: handleCloseCategoryDialog
      });
    }
  };

  const handleCloseCategoryDialog = () => {
    setShowCategoryDialog(false);
  };

  const handleDeleteConfirm = async () => {
    deleteCategory({
      id: selectedCategoriesValue?.id,
      handleClose: () => setShowDeleteDialog(false)
    });
  };

  const itemActionHandler = (value: any, type: CrudActionEnum) => {
    if (type === CrudActionEnum.Add) {
      // value is the typed string for new item
      setSelectedCategoriesValue({ name: value });
      setShowCategoryDialog(true);
    } else if (type === CrudActionEnum.Edit) {
      // value is the full option object
      setSelectedCategoriesValue(value);
      setShowCategoryDialog(true);
    } else if (type === CrudActionEnum.Delete) {
      setSelectedCategoriesValue(value);
      setShowDeleteDialog(true);
    }
  };

  useEffect(() => {
    if (selectedItem) {
      const categories = selectedItem.categories
        ? selectedItem.categories.map((item: any) => ({
            id: item._id,
            name: item.title
          }))
        : [];

      const selectedCountryList =
        selectedItem.country && countryList
          ? countryList.find((c) => c.value === selectedItem.country) || null
          : null;

      const selectedLanguageItem = selectedItem.language
        ? { id: selectedItem.language, title: selectedItem.language }
        : null;

      const selectedPlayerItem = selectedItem.player
        ? playerOptions.find((p) => p.value === selectedItem.player) ||
          playerOptions[0]
        : playerOptions[0];

      reset({
        title: selectedItem?.title || '',
        channelNumber: selectedItem?.channelNumber || '',
        language: selectedLanguageItem,
        url: selectedItem?.url || '',
        categories,
        country: selectedCountryList,
        player: selectedPlayerItem
      });

      setCategoriesList(categories);
      setSelectedCountry(selectedCountryList);
      setSelectedLanguage(selectedLanguageItem);
      setSelectedPlayer(selectedPlayerItem);
    }
  }, [selectedItem, reset, countryList]);

  useEffect(() => {
    if (!selectedItem) {
      setSelectedCountry(null);
      setSelectedLanguage(null);
      setCategoriesList([]);
      setSelectedPlayer(playerOptions[0]);
      reset({ player: playerOptions[0] });
    }
  }, [selectedItem, reset]);

  const selectedCountryList = useMemo(() => {
    return selectedCountry ? [selectedCountry] : [];
  }, [selectedCountry]);

  const selectedLanguageList = useMemo(() => {
    return selectedLanguage ? [selectedLanguage] : [];
  }, [selectedLanguage]);

  const selectedPlayerList = useMemo(() => {
    return selectedPlayer ? [selectedPlayer] : [];
  }, [selectedPlayer]);

  return (
    <div>
      <form className="w-full flex flex-col gap-1">
        <div className="mb-4">
          <div className="media-wrapper">
            <div className="flex mb-1.5">
              <span className="reuqired-symbole">*</span>
              {t('label_channel_image')}
              <InfoTooltip text="tooltip_info_live_tv_channel_image" />
            </div>

            <MediaLibraryComponent
              mediaKey={'image'}
              width={240}
              height={120}
              allowedTypes={[FileTypeEnum.Image]}
              path={selectedItem?.image}
              onSelect={(media) =>
                setSelectedMedia((prev) => ({ ...prev, image: media }))
              }
            />
          </div>
        </div>
        <div className="w-full flex gap-4">
          <div className="w-full flex flex-col gap-2">
            <FormControl error={!!errors.title}>
              <FormLabel className="flex">
                <span className="reuqired-symbole">*</span>
                {t('label_channel_title')}
                <InfoTooltip text="tooltip_info_live_tv_name" />
              </FormLabel>
              <OutlinedInput
                {...register('title', {
                  required: true,
                  validate: {
                    notOnlySpaces: validateNotOnlySpaces
                  }
                })}
                error={!!errors.title}
                placeholder={t('placeholder_live_tv_channel_title')}
              />
              <FormHelperText>
                {errors?.title &&
                  getErrorMessage({
                    errors,
                    fieldName: 'title',
                    errorMessages: liveTvErrorMessages
                  })}
              </FormHelperText>
            </FormControl>

            <FormControl error={!!errors.channelNumber}>
              <FormLabel className="flex">
                <span className="reuqired-symbole">*</span>
                {t('label_channel_number')}
                <InfoTooltip text="tooltip_info_live_tv_channel_number" />
              </FormLabel>
              <OutlinedInput
                type="number"
                className="no-spinner"
                {...register('channelNumber', {
                  required: true,
                  valueAsNumber: true
                })}
                error={!!errors.channelNumber}
                placeholder={t('placeholder_live_tv_channel_number')}
              />
              <FormHelperText>
                {errors?.channelNumber &&
                  getErrorMessage({
                    errors,
                    fieldName: 'channelNumber',
                    errorMessages: liveTvErrorMessages
                  })}
              </FormHelperText>
            </FormControl>

            <AppAutocompleteWithActions
              name="categories"
              control={control}
              placeholder={t('placeholder_live_tv_channel_category')}
              label={
                <span className="flex">
                  <span className="reuqired-symbole">*</span>
                  {t('label_live_tv_category')}
                  <InfoTooltip text="tooltip_info_channel_catgory" />
                </span>
              }
              options={categoryList || []}
              selectedList={categoriesList}
              onChange={setCategoriesList}
              itemActionHandler={itemActionHandler}
              validationProps={{
                errors,
                errorMessages: liveTvErrorMessages,
                getErrorMessage,
                validationRules: {
                  required: true
                }
              }}
            />

            <AppAutocompleteV2
              name="player"
              control={control}
              placeholder={t('placeholder_player')}
              selectedList={selectedPlayerList}
              label={
                <span className="flex">
                  <span className="reuqired-symbole">*</span>
                  {t('label_player')}
                  <InfoTooltip text="tooltip_info_player" />
                </span>
              }
              options={playerOptions}
              validationProps={{
                errors,
                errorMessages: liveTvErrorMessages,
                getErrorMessage,
                validationRules: {
                  required: true
                }
              }}
            />
          </div>
          <div className="w-full flex flex-col gap-2">
            <FormControl error={!!errors.url}>
              <FormLabel className="flex">
                <span className="reuqired-symbole">*</span>
                {t('label_live_tv_channel_url')}
                <InfoTooltip text="tooltip_info_live_tv_url" />
              </FormLabel>
              <OutlinedInput
                {...register('url', {
                  required: true,
                  pattern:
                    /^(https?|udp):\/\/([a-zA-Z0-9.-]+)(\.[a-zA-Z]{2,})?(:\d+)?(\/[^\s]*)?$/i,
                  validate: {
                    notOnlySpaces: validateNotOnlySpaces
                  }
                })}
                error={!!errors.url}
                placeholder={t('placeholder_live_tv_channel_url')}
              />
              <FormHelperText>
                {errors?.url &&
                  getErrorMessage({
                    errors,
                    fieldName: 'url',
                    errorMessages: liveTvErrorMessages
                  })}
              </FormHelperText>
            </FormControl>

            <AppAutocompleteV2
              name="country"
              control={control}
              placeholder={t('placeholder_live_tv_channel_country')}
              selectedList={selectedCountryList}
              label={
                <span className="flex items-center">
                  <span className="reuqired-symbole">*</span>
                  {t('label_live_tv_channel_country')}
                  <InfoTooltip text="tooltip_info_live_tv_country" />
                </span>
              }
              options={countryList}
              validationProps={{
                errors,
                errorMessages: liveTvErrorMessages,
                getErrorMessage,
                validationRules: {
                  required: true
                }
              }}
            />
            <AppAutocompleteV2
              name="language"
              control={control}
              placeholder={t('placeholder_live_tv_channel_language')}
              selectedList={selectedLanguageList}
              label={
                <span className="flex items-center">
                  <span className="reuqired-symbole">*</span>
                  {t('label_live_tv_channel_language')}
                  <InfoTooltip text="tooltip_info_live_tv_langauge" />
                </span>
              }
              options={countryList}
              validationProps={{
                errors,
                errorMessages: liveTvErrorMessages,
                getErrorMessage,
                validationRules: {
                  required: true
                }
              }}
            />
          </div>
        </div>

        <div className="flex justify-end items-center gap-6 pt-4 pb-1 sticky bottom-0 bg-white">
          <Button
            disabled={!isValid}
            onClick={handleSubmit(handleSave)}
            variant="contained"
            loading={isLoading()}
          >
            {t('save')}
          </Button>
        </div>
      </form>

      <AppDialog
        open={showCategoryDialog}
        title={t('entertainment_add_category_title')}
        content={
          <CategoryDialog
            loading={isCategoryLoading()}
            selectedValue={selectedCategoriesValue}
            handleSave={handleSaveCategory}
          />
        }
        onClose={() => {
          setShowCategoryDialog(false);
        }}
      />

      <div className="w-full h-full">
        <DeleteDialog
          onCancel={() => {
            setShowDeleteDialog(false);
          }}
          onDelete={handleDeleteConfirm}
          open={showDeleteDialog}
          isLoading={isCategoryLoading()}
          title={t('delete_popup_title')}
        />
      </div>
    </div>
  );
};

export default LiveTvDialogForm;
